import { createSlice } from '@reduxjs/toolkit';

import axios from 'utils/axios';

// Only log to console when in browser or when server is in development (avoid server logs in production)
const shouldLog = typeof window !== 'undefined' || process.env.NODE_ENV !== 'production';

const initialState = {
    error: null,
    postDetail: null,
    notFound: false
};

const slice = createSlice({
    name: 'postDetail',
    initialState,
    reducers: {
        hasError(state, action) {
            state.error = action.payload;
        },
        setNotFound(state, action) {
            state.notFound = action.payload;
        },
        setPostDetail(state, action) {
            state.postDetail = action.payload;
            state.error = null;
            state.notFound = false;
        },
        unsetPostDetail(state, action) {
            state.postDetail = null;
            state.error = null;
            state.notFound = false;
        }
    }
});

export default slice.reducer;

export function getBlogPost(data, callback) {
    return async (dispatch, getState) => {
        try {
            const response = await axios.get(`/api/post/detail/${data}`, {});

            // CRITICAL: Validate that the response is JSON, not HTML
            // The API might return HTML (404 page) instead of JSON, which would break everything
            const contentType = response.headers['content-type'] || '';
            const isJson = contentType.includes('application/json') || contentType.includes('text/json');

            // Check if response.data is actually an object (JSON) or a string (HTML)
            const isObject = response.data && typeof response.data === 'object' && !Array.isArray(response.data);
            const isString = typeof response.data === 'string';

            // If we got HTML instead of JSON, treat it as an error
            if (isString && response.data.trim().startsWith('<!')) {
                const error = new Error('API returned HTML instead of JSON - likely a 404 or error page');
                error.response = {
                    status: 404,
                    statusText: 'Not Found',
                    data: { message: 'Blog post not found' }
                };
                error.isHtmlResponse = true;
                if (shouldLog) {
                    console.error('[API] getBlogPost - Received HTML instead of JSON:', {
                        url: `/api/post/detail/${data}`,
                        contentType,
                        dataPreview: response.data.substring(0, 200),
                        isString,
                        isObject
                    });
                }
                dispatch(slice.actions.setNotFound(true));
                dispatch(
                    slice.actions.hasError({
                        status: 404,
                        message: error.response.data.message
                    })
                );
                throw error;
            }

            // Validate that we have a proper JSON object
            if (!isObject) {
                const error = new Error('API response is not a valid JSON object');
                error.response = {
                    status: 500,
                    statusText: 'Invalid Response',
                    data: { message: 'API returned invalid data format' }
                };
                if (shouldLog) {
                    console.error('[API] getBlogPost - Response is not a valid object:', {
                        url: `/api/post/detail/${data}`,
                        contentType,
                        dataType: typeof response.data,
                        isArray: Array.isArray(response.data),
                        dataPreview:
                            typeof response.data === 'string'
                                ? response.data.substring(0, 200)
                                : JSON.stringify(response.data).substring(0, 200)
                    });
                }
                dispatch(slice.actions.setNotFound(false));
                dispatch(
                    slice.actions.hasError({
                        status: 500,
                        message: error.response.data.message
                    })
                );
                throw error;
            }

            // Additional validation: payload must contain meaningful post content.
            // Some invalid IDs can return an object shell without real article fields.
            const hasRenderablePostData = Boolean(response.data.postTitle || response.data.postContent);
            if (!hasRenderablePostData) {
                const error = new Error('Blog post not found');
                error.status = 404;
                if (shouldLog) {
                    console.warn('[API] getBlogPost - Response object missing renderable fields:', {
                        hasId: !!response.data.id,
                        hasPostTitle: !!response.data.postTitle,
                        hasPostContent: !!response.data.postContent,
                        keys: Object.keys(response.data).slice(0, 10)
                    });
                }
                dispatch(slice.actions.setNotFound(true));
                dispatch(
                    slice.actions.hasError({
                        status: 404,
                        message: 'Blog post not found'
                    })
                );
                throw error;
            }

            dispatch(slice.actions.setPostDetail(response.data));
            if (callback) {
                callback();
            }
            return response.data;
        } catch (e) {
            const status = e?.status || e?.response?.status || null;
            const wasAlreadyNotFound = getState?.()?.postDetail?.notFound === true;
            dispatch(slice.actions.setNotFound(status === 404 || wasAlreadyNotFound));
            dispatch(
                slice.actions.hasError({
                    status,
                    message: e?.message || e?.response?.data?.message || 'Unknown error'
                })
            );
            throw e;
        }
    };
}

export function clearPostInStore(data, callback) {
    return (dispatch) => {
        dispatch(slice.actions.unsetPostDetail());
        if (callback) {
            callback();
        }
        return null;
    };
}
